import React, { useState } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  Box,
  Typography,
  Button,
  Dialog,
  DialogContent,
  CircularProgress,
  alpha,
  Divider,
} from '@mui/material';
import { Bell, CheckCheck, X, ArrowUpRight, BellOff } from 'lucide-react';
import { useNotifications, useUnreadCount, useMarkAsRead, useMarkAllAsRead } from '../../hooks/useNotifications';
import { Notification, NotificationType } from '../../types/communication';
import { NOTIFICATION_CONFIG } from '../../constants/communication';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../router/routes';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatTime = (date: Date | string) => {
  const diff = Date.now() - new Date(date).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (d < 7) return `${d}d ago`;
  return new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const getTypeColor = (type: NotificationType) => NOTIFICATION_CONFIG[type]?.color || '#8A70D6';
const getTypeIcon  = (type: NotificationType) => NOTIFICATION_CONFIG[type]?.icon  || '📢';
const getTypeBg    = (type: NotificationType) => NOTIFICATION_CONFIG[type]?.bgColor || '#EDE7F6';

// ─── Notification row ─────────────────────────────────────────────────────────
interface RowProps {
  notification: Notification;
  onMarkRead: (id: string) => void;
  onOpenDetail: (n: Notification) => void;
}

const NotifRow: React.FC<RowProps> = ({ notification, onMarkRead, onOpenDetail }) => {
  const typeColor = getTypeColor(notification.type);

  const handleClick = () => {
    if (!notification.isRead) onMarkRead(notification.id);
    onOpenDetail(notification);
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        px: '16px',
        py: '12px',
        cursor: 'pointer',
        borderLeft: `3px solid ${typeColor}`,
        bgcolor: '#fff',
        transition: 'background 0.15s',
        '&:hover': { bgcolor: '#F8F7FF' },
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: '10px',
          bgcolor: getTypeBg(notification.type),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.1rem',
          flexShrink: 0,
        }}
      >
        {getTypeIcon(notification.type)}
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '0.8125rem',
              color: '#1A1B23',
              lineHeight: 1.35,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
            }}
          >
            {notification.title}
          </Typography>
          <Typography sx={{ fontSize: '0.6875rem', color: '#9CA3AF', flexShrink: 0, mt: '1px' }}>
            {formatTime(notification.createdAt)}
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize: '0.75rem',
            color: '#6B7280',
            mt: '3px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.5,
          }}
        >
          {notification.message}
        </Typography>
      </Box>

      {/* Unread dot */}
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: typeColor,
          flexShrink: 0,
          mt: '5px',
        }}
      />
    </Box>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
export const NotificationBell: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  // Fetch only unread for the popup (max 8)
  const { data: notificationsData, isLoading } = useNotifications({ page: 1, limit: 8, isRead: false });
  const { data: unreadData } = useUnreadCount();
  const { mutate: markAsRead } = useMarkAsRead();
  const { mutate: markAllAsRead } = useMarkAllAsRead();

  const unreadNotifs = notificationsData?.notifications ?? [];
  const unreadCount  = unreadData?.unreadNotifications ?? 0;

  const handleOpen  = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleViewAll = () => {
    navigate(ROUTES.NOTIFICATIONS.LIST);
    handleClose();
  };

  return (
    <>
      {/* ── Bell button — white icon visible on purple header ── */}
      <IconButton
        onClick={handleOpen}
        aria-label="Notifications"
        sx={{
          color: '#8A70D6',
          bgcolor: '#EDE7F6',
          border: '1px solid #D8CCFF',
          '&:hover': {
            bgcolor: '#DDD5F8',
            borderColor: '#C4B8F5',
          },
        }}
      >
        <Badge
          badgeContent={unreadCount > 0 ? unreadCount : null}
          sx={{
            '& .MuiBadge-badge': {
              bgcolor: '#EF4444',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.6rem',
              minWidth: 16,
              height: 16,
              borderRadius: '8px',
            },
          }}
        >
          <Bell size={20} />
        </Badge>
      </IconButton>

      {/* ── Popup ── */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableScrollLock
        PaperProps={{
          elevation: 0,
          sx: {
            width: 360,
            bgcolor: '#fff',
            borderRadius: '16px',
            border: '1px solid #E5E7EB',
            boxShadow: '0 8px 40px rgba(138,112,214,0.15), 0 2px 8px rgba(0,0,0,0.08)',
            mt: 1.5,
            overflow: 'hidden',
          },
        }}
        MenuListProps={{ disablePadding: true }}
      >

        {/* ── 1. View all — top banner ── */}
        <Box
          onClick={handleViewAll}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: '16px',
            py: '12px',
            background: 'linear-gradient(90deg, #8A70D6 0%, #A78EE8 100%)',
            cursor: 'pointer',
            transition: 'filter 0.2s',
            '&:hover': { filter: 'brightness(1.08)' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '9px',
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Bell size={15} color="#fff" />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  color: '#fff',
                  lineHeight: 1.2,
                }}
              >
                View all notifications
              </Typography>
              {unreadCount > 0 && (
                <Typography sx={{ fontSize: '0.6875rem', color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>
                  {unreadCount} unread
                </Typography>
              )}
            </Box>
          </Box>
          <ArrowUpRight size={16} color="rgba(255,255,255,0.85)" />
        </Box>

        {/* ── 2. Unread header row ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: '16px',
            pt: '12px',
            pb: '8px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '0.75rem',
                color: '#374151',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              Unread
            </Typography>
            {unreadCount > 0 && (
              <Box
                sx={{
                  px: '6px',
                  height: 18,
                  borderRadius: '5px',
                  bgcolor: alpha('#8A70D6', 0.12),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography sx={{ fontSize: '0.6875rem', fontWeight: 800, color: '#8A70D6' }}>
                  {unreadCount}
                </Typography>
              </Box>
            )}
          </Box>

          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<CheckCheck size={12} />}
              onClick={() => markAllAsRead()}
              sx={{
                fontWeight: 600,
                fontSize: '0.6875rem',
                color: '#6B7280',
                textTransform: 'none',
                p: '3px 8px',
                borderRadius: '6px',
                minWidth: 'auto',
                '&:hover': { bgcolor: '#F3F4F6', color: '#374151' },
              }}
            >
              Mark all read
            </Button>
          )}
        </Box>

        {/* ── 3. List ── */}
        <Box
          sx={{
            maxHeight: 330,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '1px',
            bgcolor: '#F3F4F6',
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#E5E7EB', borderRadius: '4px' },
          }}
        >
          {isLoading ? (
            <Box sx={{ py: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff' }}>
              <CircularProgress size={24} sx={{ color: '#8A70D6' }} />
            </Box>
          ) : unreadNotifs.length === 0 ? (
            <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', bgcolor: '#fff' }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '14px',
                  bgcolor: '#F3F4F6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <BellOff size={22} color="#D1D5DB" />
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', color: '#374151' }}>
                  All caught up!
                </Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: '#9CA3AF', mt: '2px' }}>
                  No unread notifications
                </Typography>
              </Box>
            </Box>
          ) : (
            unreadNotifs.map((n) => (
              <NotifRow
                key={n.id}
                notification={n}
                onMarkRead={(id) => markAsRead(id)}
                onOpenDetail={setSelectedNotification}
              />
            ))
          )}
        </Box>

        {/* ── 4. "More unread" footer ── */}
        {unreadCount > unreadNotifs.length && (
          <>
            <Divider />
            <Box
              onClick={handleViewAll}
              sx={{
                px: '16px',
                py: '10px',
                bgcolor: '#FAFAFA',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                '&:hover': { bgcolor: '#F3F4F6' },
              }}
            >
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#8A70D6' }}>
                +{unreadCount - unreadNotifs.length} more — view all
              </Typography>
              <ArrowUpRight size={13} color="#8A70D6" />
            </Box>
          </>
        )}
      </Menu>

      {/* ── Detail dialog ── */}
      <Dialog
        open={!!selectedNotification}
        onClose={() => setSelectedNotification(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: '20px',
            border: '1px solid #E5E7EB',
            boxShadow: '0 20px 60px rgba(138,112,214,0.18)',
            overflow: 'hidden',
          },
        }}
      >
        {selectedNotification && (
          <DialogContent sx={{ p: 0 }}>
            {/* Coloured top stripe */}
            <Box
              sx={{
                height: 5,
                background: `linear-gradient(90deg, ${getTypeColor(selectedNotification.type)}, ${alpha(getTypeColor(selectedNotification.type), 0.3)})`,
              }}
            />

            <Box sx={{ p: '24px' }}>
              {/* Header */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '14px', mb: '20px', pr: '32px' }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '14px',
                    bgcolor: getTypeBg(selectedNotification.type),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    flexShrink: 0,
                  }}
                >
                  {getTypeIcon(selectedNotification.type)}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.0625rem', color: '#111827', lineHeight: 1.3 }}>
                    {selectedNotification.title}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF', mt: '4px' }}>
                    {formatTime(selectedNotification.createdAt)}
                  </Typography>
                </Box>
              </Box>

              <IconButton
                onClick={() => setSelectedNotification(null)}
                size="small"
                sx={{
                  position: 'absolute',
                  right: 16,
                  top: 20,
                  color: '#6B7280',
                  '&:hover': { bgcolor: '#F3F4F6', color: '#111827' },
                }}
              >
                <X size={18} />
              </IconButton>

              {/* Message body */}
              <Box
                sx={{
                  bgcolor: '#F9FAFB',
                  borderRadius: '12px',
                  border: '1px solid #E5E7EB',
                  p: '16px',
                  mb: selectedNotification.metadata ? '12px' : 0,
                }}
              >
                <Typography sx={{ fontSize: '0.9375rem', color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                  {selectedNotification.message}
                </Typography>
              </Box>

              {/* Metadata */}
              {selectedNotification.metadata && (
                <Box sx={{ bgcolor: '#F9FAFB', borderRadius: '10px', border: '1px solid #E5E7EB', p: '12px 14px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {selectedNotification.metadata.sender && (
                    <Box sx={{ display: 'flex', gap: '8px' }}>
                      <Typography sx={{ fontSize: '0.8125rem', color: '#9CA3AF', fontWeight: 600, minWidth: 56 }}>From</Typography>
                      <Typography sx={{ fontSize: '0.8125rem', color: '#374151' }}>{String(selectedNotification.metadata.sender)}</Typography>
                    </Box>
                  )}
                  {selectedNotification.metadata.category && (
                    <Box sx={{ display: 'flex', gap: '8px' }}>
                      <Typography sx={{ fontSize: '0.8125rem', color: '#9CA3AF', fontWeight: 600, minWidth: 56 }}>Category</Typography>
                      <Typography sx={{ fontSize: '0.8125rem', color: '#374151' }}>{String(selectedNotification.metadata.category)}</Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Close */}
              {(() => {
                const dest =
                  selectedNotification.actionUrl ||
                  (selectedNotification.type === NotificationType.SYLLABUS_PENDING
                    ? ROUTES.EXAM.TODOS
                    : selectedNotification.type === NotificationType.SCORES_PENDING
                      ? ROUTES.EXAM.ENTER_SCORES
                      : selectedNotification.type === NotificationType.EXAM_SCHEDULE
                        ? ROUTES.EXAM.MY_EXAMS
                        : selectedNotification.type === NotificationType.MESSAGE
                          ? ROUTES.COMMUNICATION.MESSAGES
                          : null);
                return dest ? (
                  <Button
                    fullWidth
                    onClick={() => {
                      setSelectedNotification(null);
                      navigate(dest);
                    }}
                    sx={{
                      mt: '20px',
                      fontWeight: 700,
                      fontSize: '0.875rem',
                      textTransform: 'none',
                      py: '11px',
                      borderRadius: '12px',
                      bgcolor: '#8A70D6',
                      color: '#fff',
                      boxShadow: 'none',
                      '&:hover': { bgcolor: '#7560C4', boxShadow: 'none' },
                    }}
                  >
                    Open
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    onClick={() => setSelectedNotification(null)}
                    sx={{
                      mt: '20px',
                      fontWeight: 700,
                      fontSize: '0.875rem',
                      textTransform: 'none',
                      py: '11px',
                      borderRadius: '12px',
                      bgcolor: '#8A70D6',
                      color: '#fff',
                      boxShadow: 'none',
                      '&:hover': { bgcolor: '#7560C4', boxShadow: 'none' },
                    }}
                  >
                    Dismiss
                  </Button>
                );
              })()}
            </Box>
          </DialogContent>
        )}
      </Dialog>
    </>
  );
};
